import { gql, useQuery } from '@apollo/client';
import IconRun from '@apollo/icons/default/IconRun.svg';
import { Button } from '@apollo/orbit';
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { CloudRouterDetails } from 'src/app/graph/variantsHomepage/cloudRouterDetails/CloudRouterDetails';
import { CloudRouterLifeCycleAlert } from 'src/app/graph/variantsHomepage/cloudRouterLifeCycleAlerts/CloudRouterLifeCycleAlerts';
import { Loading } from 'src/components/common/loading/Loading';
import { MarbledAvatar } from 'src/components/common/MarbledAvatar';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useIdentity } from 'src/hooks/useIdentity';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';

import { SchemaTagDiffFragment } from '../changeLogPage/schemaTagDiffFragment/SchemaTagDiffFragment';
import { useSchema } from '../hooks/useSchema';
import * as routes from '../routes';
import { NoSchemaFoundCard } from '../shared/NoSchemaFoundCard';

import { ChangeLogSection } from './changeLogSection/ChangeLogSection';
import {
  shouldShowCloudRouterAlerts,
  useRouterStatusTransition,
} from './cloudRouterLifeCycleAlerts/helpers';
import {
  ContractHomePageGraphVariantFragment,
  ContractSection,
} from './contractSection/ContractSection';
import {
  GraphHeaderData,
  GraphHeaderDataServiceFragment,
  GraphVariantOrderFragment,
  GraphVariantRouterFragment,
} from './graphHeader/GraphHeaderData';
import {
  LinksContentGraphVariantFragment,
  LinksSection,
} from './linksSection/LinksSection';
import { NewToGraphOSStudioSection } from './newToGraphOSStudioSection/NewToGraphOSStudioSection';
import { ReadmeCard, readmeCardReadmeFragment } from './readmeCard/ReadmeCard';
import { readmeMarkdownServiceFragment } from './readmeMarkdown/ReadmeMarkdown';

const VariantsHomepageQuery = gql`
  query VariantsHomepageQuery(
    $graphId: ID!
    $graphVariant: String!
    $isCloudSupergraph: Boolean!
  ) {
    service(id: $graphId) {
      id
      title
      avatarUrl(size: 100)
      variant(name: $graphVariant) {
        id
        name
        isContract
        readme {
          id
          ...ReadmeCardReadmeFragment
        }
        ...ContractHomePageGraphVariantFragment
        ...LinksContentGraphVariantFragment
        ...GraphVariantRouterFragment @include(if: $isCloudSupergraph)
        ...GraphVariantOrderFragment @include(if: $isCloudSupergraph)
        activeSchemaPublish {
          id
          ... on SchemaTag @defer {
            history(limit: 1, includeUnchanged: false) {
              id
              createdAt
              ...SchemaTagDiffFragment
            }
          }
        }
      }
      ...GraphHeaderDataServiceFragment
      ...ReadmeMarkdownServiceFragment
    }
  }
  ${GraphVariantRouterFragment}
  ${GraphVariantOrderFragment}
  ${GraphHeaderDataServiceFragment}
  ${readmeMarkdownServiceFragment}
  ${readmeCardReadmeFragment}
  ${ContractHomePageGraphVariantFragment}
  ${LinksContentGraphVariantFragment}
  ${SchemaTagDiffFragment}
`;

class VariantsHomepageError extends Error {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, VariantsHomepageError.prototype);
    this.name = this.constructor.name;
  }
}

export const VariantsHomepage = ({
  isCloudSupergraph,
}: {
  isCloudSupergraph: boolean;
}) => {
  const { graphId, graphVariant } = useRouteParams(routes.variantRouteConfig);
  const graphRef = useMemo(
    () => ({ graphId, graphVariant }),
    [graphId, graphVariant],
  );
  const location = useLocation();
  const { data, error, loading } = useQuery<
    GraphQLTypes.VariantsHomepageQuery,
    GraphQLTypes.VariantsHomepageQueryVariables
  >(VariantsHomepageQuery, {
    variables: { graphId, graphVariant, isCloudSupergraph },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const { loadingSchema, error: schemaError, activeSchema } = useSchema();
  const { me, meLoading } = useIdentity();

  const graph = data?.service;
  const variant = graph?.variant;
  const schemaHistory = graph?.variant?.activeSchemaPublish?.history;
  const schemaHistoryLoading =
    loading ||
    !!(graph?.variant?.activeSchemaPublish && schemaHistory === undefined);
  const router = graph?.variant?.router;
  const latestLaunch = graph?.variant?.latestLaunch;
  const latestApprovedLaunch = graph?.variant?.latestApprovedLaunch;

  const shouldShowCloudRouterDetails = useLDFlag(
    'betelgeuse-serverless-aws-ui',
  );
  const cloudRouterAlertsEnabled = useLDFlag('betelgeuse-serverless-aws-ui');

  const { routerIsRecreated, routerRecreateIsErrored } =
    useRouterStatusTransition(router?.status);

  useTrackL1PageView('variantsHomeL1', { graphRef });

  if (loading || loadingSchema || meLoading) {
    return <Loading />;
  } else if (error) {
    throw error;
  } else if (!graph) {
    throw new VariantsHomepageError('Missing graph');
  } else if (!variant) {
    throw new VariantsHomepageError('Missing variant');
  }

  return !activeSchema ? (
    <>
      <PageTitle title="README" />
      <NoSchemaFoundCard
        graphRef={graphRef}
        schemaError={schemaError}
        isContract={variant.isContract}
      />
    </>
  ) : (
    <PageLayout>
      <div className="mx-auto grid max-w-[1400px] grid-cols-[minmax(500px,2fr)_minmax(100px,432px)] gap-4">
        <div>
          <div className="flex pb-3 pl-8 pr-2 pt-7">
            <div className="mr-4 flex-none">
              {graph.avatarUrl ? (
                <img
                  src={graph.avatarUrl}
                  alt={`${graph.title} avatar`}
                  className="size-12 rounded-full"
                />
              ) : (
                <MarbledAvatar animate={true} identifier={graph.id} />
              )}
            </div>
            <GraphHeaderData
              graph={graph}
              graphRef={graphRef}
              isCloudSupergraph={isCloudSupergraph}
              router={router}
              latestLaunch={latestLaunch}
              latestApprovedLaunch={latestApprovedLaunch}
              lastUpdateAt={
                graph.variant?.activeSchemaPublish?.history?.[0]?.createdAt
              }
            />
          </div>
          {cloudRouterAlertsEnabled && shouldShowCloudRouterAlerts(router) && (
            <div className="flex pb-3 pl-8 pr-2 pt-7">
              <CloudRouterLifeCycleAlert
                router={router}
                graphRef={graphRef}
                launchId={latestApprovedLaunch?.id}
                routerIsRecreated={routerIsRecreated}
                routerRecreateIsErrored={routerRecreateIsErrored}
              />
            </div>
          )}
          <ReadmeCard
            graphRef={graphRef}
            readme={variant.readme}
            shortcodesData={graph}
            loading={loading}
            className="mb-8 ml-8 mr-2 mt-4"
            canEditProposal={false}
          />
        </div>
        <div className="w-[432px] min-w-[432px] pr-8 pt-20">
          <div className="mb-10 flex">
            <Tooltip label="Open this graph in Explorer to run an operation">
              <Button
                as={Link}
                to={routes.explorerRouteConfig.locationFrom({
                  location,
                  fromRouteConfig: routes.variantHomepageRouteConfig,
                })}
                className="mt-1.5"
                size="lg"
                variant="primary"
                leftIcon={<IconRun className="w-2" />}
                data-analytics-label="Run in Explorer (big button)"
                data-analytics-category="Variant Home"
                data-testid="readme_run_in_explorer"
              >
                Run in Explorer
              </Button>
            </Tooltip>
          </div>
          {router && shouldShowCloudRouterDetails && (
            <div className="mb-10 flex w-full">
              <CloudRouterDetails graphRef={graphRef} router={router} />
            </div>
          )}
          <div className="flex flex-col gap-6">
            {variant.sourceVariant && variant.contractFilterConfig && (
              <ContractSection
                graphRef={graphRef}
                contractFilterConfig={variant.contractFilterConfig}
                sourceVariant={variant.sourceVariant}
              />
            )}
            <LinksSection graphRef={graphRef} links={variant.links} />
            <ChangeLogSection
              schemaHistory={schemaHistory}
              graphqlSchema={activeSchema}
              schemaHistoryLoading={schemaHistoryLoading}
            />
            <NewToGraphOSStudioSection userId={me?.id} />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
